import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import HolidayCard from '../Card/HolidayCard';
import { listParametres } from '../../api/restaurant';
import { loadParams } from '../../tools/utils';

interface HolidayPeriod {
  name: string;
  start: string; // ISO date
  end: string;   // ISO date
}

// Mapped by academic start year and zone (A/B/C)
// structure: VACANCES_BY_ACADEMIC[academicStartYear][zone]
const VACANCES_BY_ACADEMIC_AND_ZONE: Record<number, Record<string, HolidayPeriod[]>> = {
  2025: {
    // Zone A (sample) - replace with authoritative dates if needed
    A: [
      { name: "Vacances de la Toussaint", start: '2025-10-18', end: '2025-11-03' },
      { name: "Vacances de Noël", start: '2025-12-20', end: '2026-01-05' },
      { name: "Vacances d'hiver", start: '2026-02-07', end: '2026-02-23' },
      { name: "Vacances de printemps", start: '2026-04-04', end: '2026-04-20' },
      { name: "Vacances d'été", start: '2026-07-04', end: '2026-09-01' },
    ],
    // Zone B (Moselle) - default
    B: [
      { name: "Vacances de la Toussaint", start: '2025-10-18', end: '2025-11-03' },
      { name: "Vacances de Noël", start: '2025-12-20', end: '2026-01-05' },
      { name: "Vacances d'hiver", start: '2026-02-14', end: '2026-03-02' },
      { name: "Vacances de printemps", start: '2026-04-11', end: '2026-04-27' },
      { name: "Vacances d'été", start: '2026-07-04', end: '2026-09-01' },
    ],
    // Zone C (sample)
    C: [
      { name: "Vacances de la Toussaint", start: '2025-10-18', end: '2025-11-03' },
      { name: "Vacances de Noël", start: '2025-12-20', end: '2026-01-05' },
      { name: "Vacances d'hiver", start: '2026-02-21', end: '2026-03-09' },
      { name: "Vacances de printemps", start: '2026-04-18', end: '2026-05-04' },
      { name: "Vacances d'été", start: '2026-07-04', end: '2026-09-01' },
    ],
  },
  2026: {
    A: [
      { name: "Vacances de la Toussaint", start: '2026-10-17', end: '2026-11-02' },
      { name: "Vacances de Noël", start: '2026-12-19', end: '2027-01-04' },
      { name: "Vacances d'hiver", start: '2027-02-13', end: '2027-03-01' },
      { name: "Vacances de printemps", start: '2027-04-10', end: '2027-04-26' },
      { name: "Vacances d'été", start: '2027-07-03', end: '2027-09-01' },
    ],
    B: [
      { name: "Vacances de la Toussaint", start: '2026-10-17', end: '2026-11-02' },
      { name: "Vacances de Noël", start: '2026-12-19', end: '2027-01-04' },
      { name: "Vacances d'hiver", start: '2027-02-20', end: '2027-03-08' },
      { name: "Vacances de printemps", start: '2027-04-17', end: '2027-05-03' },
      { name: "Vacances d'été", start: '2027-07-03', end: '2027-09-01' },
    ],
    C: [
      { name: "Vacances de la Toussaint", start: '2026-10-17', end: '2026-11-02' },
      { name: "Vacances de Noël", start: '2026-12-19', end: '2027-01-04' },
      { name: "Vacances d'hiver", start: '2027-02-06', end: '2027-02-22' },
      { name: "Vacances de printemps", start: '2027-04-03', end: '2027-04-19' },
      { name: "Vacances d'été", start: '2027-07-03', end: '2027-09-01' },
    ],
  }
};

const computeAcademicStartYear = (now = new Date()) => {
  // If month >= September (8) => academic year starts this calendar year
  // else academic year started previous calendar year
  return now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
};

const VacancesMoselle: React.FC = () => {
  const [academicStartYear, setAcademicStartYear] = useState<number>(() => computeAcademicStartYear());
  const [zone, setZone] = useState<string>(); 

  useEffect(() => {
    const loadZone = async () => {
      const params = await loadParams();
      setZone(params.get("zone") || ""); // default to B if not set
    };

    loadZone();
  }, []);

  const periods = useMemo(() => {
    if (!zone) return [];
    const byYear = VACANCES_BY_ACADEMIC_AND_ZONE[academicStartYear] || {};
    return byYear[zone] || [];
  }, [academicStartYear, zone]);

  const reloadNow = useCallback(() => {
    // For now, just update the state so the component re-reads mapping (could fetch remote data here)
    setAcademicStartYear(computeAcademicStartYear());
  }, []);

  useEffect(() => {
    // Check once per day whether it's Sept 1 — if so, reload the academic year mapping
    const check = () => {
      const now = new Date();
      if (now.getMonth() === 8 && now.getDate() === 1) {
        setAcademicStartYear(computeAcademicStartYear(now));
      }
    };

    // run immediate check
    check();

    const dayMs = 24 * 60 * 60 * 1000;
    const id = setInterval(check, dayMs);
    return () => clearInterval(id);
  }, []);

  const [active, setActive] = useState<number>(0);

  useEffect(() => {
    // when periods change, set the active slide to the next upcoming period
    if (!periods || periods.length === 0) {
      setActive(0);
      return;
    }

    const now = new Date();
    // find first period whose end date is >= today (covers ongoing or upcoming)
    let idx = periods.findIndex(p => {
      try {
        const end = new Date(p.end);
        return end.getTime() >= now.getTime();
      } catch (e) {
        return false;
      }
    });

    if (idx === -1) {
      // no upcoming periods -> default to first
      idx = 0;
    }
    setActive(idx);
  }, [periods]);

  const prev = () => setActive((a) => (a - 1 + periods.length) % periods.length);
  const next = () => setActive((a) => (a + 1) % periods.length);

  return (
    <Paper className="p-4" elevation={2}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-2">Périodes de vacances</h3>
          <div className="text-sm" style={{ color: 'text.secondary' }}>
            Année scolaire {academicStartYear}/{academicStartYear + 1} {periods.length === 0 ? '(Aucune donnée disponible)' : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs " style={{ color: 'text.secondary' }}>Zone</div>
          <select value={zone} onChange={(e) => setZone(e.target.value)} className="text-sm p-1 rounded">
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
          </select>
          <Button onClick={reloadNow} size="small" startIcon={<RefreshIcon />}>
            Rafraîchir
          </Button>
        </div>
      </div>

      {periods.length > 0 ? (
        <div className="flex flex-col items-center">
          <div className="flex items-center space-x-2">
            <IconButton size="small" onClick={prev} aria-label="previous" disabled={periods.length <= 1}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>

            <div style={{ minWidth: 260, maxWidth: 520, width: '100%' }}>
              <HolidayCard period={periods[active]} />
            </div>

            <IconButton size="small" onClick={next} aria-label="next" disabled={periods.length <= 1}>
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </div>

          <div className="mt-2 text-sm" style={{ color: 'text.secondary' }}>
            {active + 1} / {periods.length}
          </div>
        </div>
      ) : (
        <div className="text-sm" style={{ color: 'text.secondary' }}>
          Données non disponibles pour cette année scolaire.
        </div>
      )}
    </Paper>
  );
};

export default VacancesMoselle;
